import { Box, Card, Group, Progress, Text } from "@mantine/core";
import { useIsDark } from "@/hooks/useIsDark";
import { useTranslate } from "@/hooks/useTranslate";
import { CHART } from "@/theme";

/**
 * Penyejajaran isi kartu: pita judul dijamin setinggi minimal dua baris teks,
 * sehingga judul satu baris ("Kerukunan Umat Beragama") dan dua baris
 * ("Penyusunan Perubahan II APBDes 2026") memulai progres di ketinggian sama.
 * Tinggi diturunkan dari token font Mantine + padding pita — tidak di-tambal
 * per kartu. Judul lebih panjang tetap sejajar di bagian bawah karena footer
 * didorong ke dasar kartu dengan `mt="auto"`.
 */
const TITLE_MIN_LINES = 2;
const TITLE_LINE_HEIGHT = 1.4;
const HEADER_PADDING_Y_PX = 16;
const HEADER_MIN_HEIGHT = `calc(${TITLE_MIN_LINES} * ${TITLE_LINE_HEIGHT} * var(--mantine-font-size-md) + ${HEADER_PADDING_Y_PX * 2}px)`;

interface ActivityCardProps {
	title: string;
	date: string;
	progress: number;
	status: "SELESAI" | "BERJALAN" | "TERTUNDA";
}

export function ActivityCard({
	title,
	date,
	progress,
	status,
}: ActivityCardProps) {
	const t = useTranslate();
	const statusLabel =
		status === "SELESAI"
			? t.kinerjaDivisi.statusSelesai
			: status === "BERJALAN"
				? t.kinerjaDivisi.statusBerjalan
				: t.kinerjaDivisi.statusTertunda;
	const getStatusColor = () => {
		switch (status) {
			case "SELESAI":
				return CHART.green;
			case "BERJALAN":
				return CHART.blue;
			case "TERTUNDA":
				return CHART.red;
			default:
				return CHART.gray;
		}
	};

	const dark = useIsDark();

	return (
		<Card
			radius="xl"
			p={0}
			withBorder={false}
			style={{
				backgroundColor: dark ? "#334155" : "white",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
			}}
			h={"100%"}
		>
			{/* 🔵 HEADER */}
			<Box
				style={{
					backgroundColor: "#1E3A5F",
					padding: `${HEADER_PADDING_Y_PX}px 16px`,
					textAlign: "center",
					minHeight: HEADER_MIN_HEIGHT,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Text c="white" fw={700} size="md" lh={TITLE_LINE_HEIGHT}>
					{title}
				</Text>
			</Box>

			{/* CONTENT */}
			<Box p="md" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
				{/* PROGRESS */}
				<Group justify="space-between" mb={4}>
					<Text size="xs" c="dimmed">
						{t.kinerjaDivisi.progres}
					</Text>
					<Text size="xs" fw={600}>
						{progress}%
					</Text>
				</Group>
				<Progress
					value={progress}
					radius="xl"
					size="lg"
					color="orange"
					styles={{
						root: {
							height: 16,
						},
					}}
				/>

				{/* FOOTER */}
				<Group justify="space-between" mt="auto" pt="md">
					<Text size="sm" fw={500}>
						{date}
					</Text>

					<Box
						style={{
							backgroundColor: getStatusColor(),
							color: "white",
							padding: "4px 12px",
							borderRadius: 999,
							fontSize: 12,
							fontWeight: 600,
						}}
					>
						{statusLabel}
					</Box>
				</Group>
			</Box>
		</Card>
	);
}
